import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { Menu, X, Command, Linkedin, Github, Mail } from 'lucide-react';
import { NAV_LINKS } from '../constants';

const Layout = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const toggleMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
  const closeMenu = () => setIsMobileMenuOpen(false);

  return (
    <div className="flex flex-col min-h-screen bg-white font-sans antialiased">
      {/* Navigation */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100/50 transition-all duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group" onClick={closeMenu}>
              <div className="relative overflow-hidden">
                {/* 
                  Logo file should be placed at: public/assets/logo/nyx-logo.png
                  The logo image contains "NYX SOLUTION" text with the futuristic design.
                */}
                <img 
                  src="assets/logo/nyx.logo.jfif" 
                  alt="NyxSolutions" 
                  className="h-14 w-auto object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.nextElementSibling?.classList.remove('hidden');
                  }}
                />
                <div className="hidden bg-brand-600 p-2 text-white rounded-lg">
                  <Command size={24} />
                </div>
              </div>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center space-x-10">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`text-[15px] font-medium transition-colors duration-200 ${
                    location.pathname === link.path || (link.path !== '/' && location.pathname.startsWith(link.path))
                      ? 'text-brand-600'
                      : 'text-gray-600 hover:text-brand-600'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>


            {/* Mobile Menu Button */}
            <button
              onClick={toggleMenu}
              className="md:hidden p-2 text-gray-500 hover:bg-gray-50 rounded-md"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-gray-100 bg-white absolute w-full shadow-xl">
            <div className="px-4 py-6 space-y-4">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={closeMenu}
                  className={`block text-lg font-medium ${
                    location.pathname === link.path || (link.path !== '/' && location.pathname.startsWith(link.path))
                      ? 'text-brand-600'
                      : 'text-gray-600'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-grow">
        <Outlet />
      </main>

      {/* Footer (Minimal Contact) */}
      <footer className="bg-slate-50 border-t border-slate-200 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8 mb-12">
            <div>
               <div className="flex items-center gap-2 mb-4">
                 {/* Reusing logo logic here or just text */}
                 <span className="text-xl font-bold text-gray-900 tracking-tight">NyxSolutions</span>
               </div>
               <p className="text-gray-500 text-sm max-w-sm leading-relaxed">
                 Building production-grade AI applications for the modern enterprise.
                 <br />Mobility. Education. Workflow Automation.
               </p>
            </div>
            
            <div className="flex flex-col gap-4">
               <a href="mailto:solutionnyx@gmail.com" className="flex items-center gap-2 text-gray-700 hover:text-brand-600 font-medium transition-colors">
                 <Mail size={18} />
                 solutionnyx@gmail.com
               </a>
               <div className="flex items-center gap-4">
                 <a href="https://www.linkedin.com/company/nyx-solution09/about/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="w-10 h-10 flex items-center justify-center rounded-full bg-white border border-gray-200 text-gray-500 hover:text-brand-600 hover:border-brand-200 transition-all">
                   <Linkedin size={18} />
                 </a>
                 <a href="https://github.com/nyx-solutions-team" target="_blank" rel="noopener noreferrer" aria-label="GitHub" className="w-10 h-10 flex items-center justify-center rounded-full bg-white border border-gray-200 text-gray-500 hover:text-brand-600 hover:border-brand-200 transition-all">
                   <Github size={18} />
                 </a>
               </div>
            </div>
          </div>
          
          <div className="border-t border-gray-200 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-500">
            <div>
              &copy; {new Date().getFullYear()} NyxSolutions Inc. All rights reserved.
            </div>
            <div className="flex gap-6">
              <Link to="/privacy" className="hover:text-gray-900 transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-gray-900 transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;

